<template>
  <div class="card">
    <div class="bg-blue-light p-1">
      <TitleButton
          :showBtn="false"
          title="Payroll Journal"
      />
    </div>

    <ul class="nav nav-tabs px-2 pt-2" role="tablist">
      <li class="nav-item">
        <a
            :class="['nav-link', { active: isPendingTab }]"
            style="cursor: pointer"
            @click="onTabChange('pending')"
        >
          Pending JV
        </a>
      </li>
      <li class="nav-item">
        <a
            :class="['nav-link', { active: isCompleteTab }]"
            style="cursor: pointer"
            @click="onTabChange('complete')"
        >
          Complete JV
        </a>
      </li>
    </ul>

    <div class="px-2 mt-2">
      <div class="table-responsive" style="min-height: 400px;">
        <table class="table table-bordered">
          <thead>
          <tr>
            <th>Payroll Period</th>
            <th>Process No</th>
            <th>Process Date</th>
            <th class="text-end nowrap">Total Earning</th>
            <th class="text-end nowrap">Total Deduction</th>
            <th class="text-end nowrap">Total Net Payable</th>
            <th v-if="isPendingTab">Status</th>
            <th v-if="isCompleteTab">Voucher No</th>
            <th class="text-center">Action</th>
          </tr>
          </thead>
          <tbody>
          <tr v-for="statement in statements" :key="statement.id">
            <td class="nowrap">{{ periodLabel(statement) }}</td>
            <td>{{ statement.id }}</td>
            <td class="nowrap">{{ formatDate(statement.created_at) }}</td>
            <td class="text-end nowrap">{{ formatAmount(statement.total_earning) }}</td>
            <td class="text-end nowrap">{{ formatAmount(statement.total_deduction) }}</td>
            <td class="text-end nowrap">{{ formatAmount(statement.total_net_payable) }}</td>
            <td v-if="isPendingTab">
              <span :class="statusClass(statement.journal_status)">{{ statusLabel(statement.journal_status) }}</span>
            </td>
            <td v-if="isCompleteTab">
              <span class="fw-bold">{{ statement.voucher_no }}</span>
              <div class="text-muted small">{{ statement.voucher_status }}</div>
            </td>
            <td class="text-center">
              <button
                  v-if="statement.can_create_journal"
                  class="btn btn-sm btn-outline-primary"
                  @click="goToCreate(statement.id)"
              >
                Create JV
              </button>
              <button
                  v-else
                  class="btn btn-sm btn-outline-success"
                  @click="goToCreate(statement.id)"
              >
                View JV
              </button>
            </td>
          </tr>
          </tbody>
        </table>
        <p v-if="!hasStatements" class="text-center mt-5">{{ emptyMessage }}</p>
      </div>
    </div>

    <div class="px-2 pb-2 mt-3">
      <Pagination :offset="offset" @onPageChange="onPageChange" ref="pagination"/>
    </div>

    <Loader v-if="loading"/>
  </div>
</template>

<script setup>
import {computed, onMounted, ref} from 'vue'
import {useRoute, useRouter} from 'vue-router'
import {useStore} from 'vuex'
import TitleButton from '@/components/atom/TitleButton'
import Loader from '@/components/atom/LoaderComponent'
import Pagination from '@/components/atom/Pagination'
import handleHrPayroll from '@/services/modules/hrPayroll/hrPayroll'
import useDate from '@/services/utils/day'

const $route = useRoute()
const $router = useRouter()
const $store = useStore()

const {fetchPayrollJournals} = handleHrPayroll()
const {formatToDate} = useDate()

const loading = ref(false)
const statements = ref([])
const offset = ref(20)
const activeTab = ref('pending')

const statusOptions = [
  {name: 'Pending', value: 'pending'},
  {name: 'Created', value: 'created'},
  {name: 'Reprocess', value: 'reprocess'}
]

const hasStatements = computed(() => statements.value.length > 0)
const isPendingTab = computed(() => activeTab.value === 'pending')
const isCompleteTab = computed(() => activeTab.value === 'complete')

const tabStatus = computed(() => {
  if (isPendingTab.value) return 'pending,reprocess'
  return 'created'
})

const emptyMessage = computed(() => {
  if (isPendingTab.value) return 'No payroll statement is waiting for a journal voucher'
  return 'No journal voucher has been created yet'
})

const formatAmount = (amount) => {
  if (amount === null || amount === undefined) return '0.00'
  return Number(amount).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})
}

const formatDate = (value) => {
  if (!value) return '-'
  return formatToDate(value)
}

const periodLabel = (statement) => {
  if (!statement.payroll_start_date) return '-'
  return `${statement.payroll_start_date} - ${statement.payroll_end_date}`
}

const statusLabel = (status) => {
  const option = statusOptions.find(item => item.value === status)
  if (!option) return status
  return option.name
}

const statusClass = (status) => {
  if (status === 'created') return 'badge bg-success'
  if (status === 'reprocess') return 'badge bg-warning'
  return 'badge bg-secondary'
}

const getQuery = () => {
  let query = `?company_id=${$route.params.companyId}&offset=${offset.value}`
  query += `&journal_status=${tabStatus.value}`
  if (!$route.query.page) return query
  return `${query}&page=${$route.query.page}`
}

const setPagination = (data) => {
  if (!data.data.length) return
  $store.commit('setPaginationData', {
    records: data.total,
    perPage: data.per_page,
    currentPage: data.current_page,
  })
}

const getStatements = () => {
  loading.value = true
  statements.value = []
  fetchPayrollJournals(getQuery())
      .then(res => {
        if (!res.status) {
          $store.commit('resetPagination')
          return
        }
        statements.value = res.data.data
        setPagination(res.data)
      })
      .finally(() => loading.value = false)
}

const onPageChange = async (page) => {
  let routeQuery = Object.assign({}, $route.query)
  routeQuery.page = page
  await $router.push({path: $route.path, query: routeQuery})
  getStatements()
}

const onTabChange = async (tab) => {
  if (activeTab.value === tab) return
  activeTab.value = tab
  let routeQuery = Object.assign({}, $route.query)
  delete routeQuery.page
  await $router.push({path: $route.path, query: routeQuery})
  getStatements()
}

const goToCreate = (payrollStatementId) => {
  $router.push({
    name: 'payroll-journal-create',
    params: {
      companyId: $route.params.companyId,
      moduleId: $route.params.moduleId,
      menuId: $route.params.menuId,
      pageId: $route.params.pageId,
      payrollStatementId: payrollStatementId
    }
  })
}

onMounted(() => {
  getStatements()
})
</script>

<style scoped>
.nowrap {
  white-space: nowrap;
}

table thead th {
  white-space: nowrap;
}
</style>
